import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Building2, Shield, FileText, CheckCircle, ChevronRight } from "lucide-react";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { SEOHead } from "@/components/SEOHead";
import { Button } from "@/components/ui/button";

const verificationItems = [
  {
    label: "Corporate Entity",
    value: "OnPoint Authority Systems, Inc.",
    detail: "Delaware Corporation",
  },
  {
    label: "IP Status",
    value: "Proprietary & Documented",
    detail: "All systems formally owned",
  },
  {
    label: "Licensing Framework",
    value: "Active",
    detail: "Operator certification program",
  },
  {
    label: "Governance",
    value: "Formalized",
    detail: "Internal review & approval systems",
  },
];

const ipHoldings = [
  "Authority Systems Framework™",
  "Decision & Intake Architecture™",
  "Governance & Compliance Model™",
  "Certification & Licensing Engine™",
];

const operatingBrands = [
  {
    name: "Tessa Authority Systems™",
    details: ["Flagship authority platform", "High-value client intake", "Market-facing trust brand"],
  },
  {
    name: "Future Vertical Brands",
    details: ["Industry-specific authority systems", "White-labeled enterprise deployments"],
    future: true,
  },
];

const methodologies = [
  "Business Authority Engineering™",
  "Operator Certification Program™",
  "Authority Systems Playbooks™",
  "Governance Training Modules™",
];

const licensedOperators = [
  "Certified Authority Operators",
  "Annual License Holders",
  "Revocable Usage Rights (No IP Ownership)",
];

const CredibilityPage = () => {
  return (
    <div className="min-h-screen bg-[#080808]" data-testid="credibility-page">
      <SEOHead 
        title="Corporate & Governance Information"
        description="Corporate formation details, IP ownership statement, licensing framework summary, and buyer contact protocol."
        url="https://onpointauthority.com/credibility"
      />
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 px-6 md:px-12" data-testid="credibility-hero">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-6">
              Quiet Proof
            </p>
            <h1 className="font-serif text-5xl md:text-7xl font-light tracking-tight leading-[0.95] text-foreground mb-8">
              Corporate &<br />
              <span className="text-primary">Governance Information</span>
            </h1>
            <p className="text-lg md:text-xl font-light leading-relaxed text-muted-foreground max-w-2xl">
              This page exists to remove doubt, not generate leads.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Verification Grid */}
      <section className="py-16 md:py-24 px-6 md:px-12 border-t border-white/5" data-testid="verification-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {verificationItems.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-card/5 border border-white/5 p-8"
                data-testid={`verification-${index}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <p className="text-xs font-mono text-muted-foreground uppercase">{item.label}</p>
                  <CheckCircle className="h-4 w-4 text-green-400" strokeWidth={1.5} />
                </div>
                <p className="font-serif text-2xl text-foreground mb-2">{item.value}</p>
                <p className="text-sm text-muted-foreground">{item.detail}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* IP Hierarchy Structure */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-[#050505]" data-testid="ip-hierarchy-section">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-12"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
              Corporate Structure
            </p>
            <h2 className="font-serif text-4xl md:text-5xl font-normal tracking-tight text-foreground">
              IP & Brand<br />
              <span className="text-primary">Hierarchy</span>
            </h2>
          </motion.div>

          {/* Hierarchy Diagram */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="border border-white/10 bg-[#0a0a0a]"
          >
            {/* Root */}
            <div className="p-6 border-b border-white/10">
              <div className="flex items-center gap-4">
                <Building2 className="h-6 w-6 text-primary" strokeWidth={1.5} />
                <div>
                  <p className="font-serif text-xl text-foreground">OnPoint Authority Systems, Inc.</p>
                  <p className="text-xs text-muted-foreground">Delaware C-Corporation</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-white/10">
              {/* Core IP Holdings */}
              <div className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <ChevronRight className="h-4 w-4 text-primary" />
                  <p className="text-xs font-medium tracking-[0.15em] uppercase text-primary">Core IP Holdings</p>
                </div>
                <ul className="space-y-2 ml-6">
                  {ipHoldings.map((item, index) => (
                    <li key={index} className="flex items-center gap-2 text-sm text-foreground">
                      <div className="w-1 h-1 bg-primary rounded-full" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Operating Brands */}
              <div className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <ChevronRight className="h-4 w-4 text-primary" />
                  <p className="text-xs font-medium tracking-[0.15em] uppercase text-primary">Operating Brands</p>
                </div>
                <div className="ml-6 space-y-4">
                  {operatingBrands.map((brand, index) => (
                    <div key={index}>
                      <p className={`text-sm font-medium mb-1 ${brand.future ? 'text-muted-foreground' : 'text-foreground'}`}>
                        {brand.name} {brand.future && <span className="text-xs">(Future)</span>}
                      </p>
                      <ul className="space-y-1 ml-4">
                        {brand.details.map((detail, i) => (
                          <li key={i} className="flex items-center gap-2 text-xs text-muted-foreground">
                            <div className="w-1 h-1 bg-white/30 rounded-full" />
                            {detail}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-white/10 border-t border-white/10">
              {/* Methodologies & Products */}
              <div className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <ChevronRight className="h-4 w-4 text-primary" />
                  <p className="text-xs font-medium tracking-[0.15em] uppercase text-primary">Methodologies & Products</p>
                </div>
                <ul className="space-y-2 ml-6">
                  {methodologies.map((item, index) => (
                    <li key={index} className="flex items-center gap-2 text-sm text-foreground">
                      <div className="w-1 h-1 bg-primary rounded-full" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Licensed Operators */}
              <div className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <ChevronRight className="h-4 w-4 text-primary" />
                  <p className="text-xs font-medium tracking-[0.15em] uppercase text-primary">Licensed Operators</p>
                </div>
                <ul className="space-y-2 ml-6">
                  {licensedOperators.map((item, index) => (
                    <li key={index} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <div className="w-1 h-1 bg-white/30 rounded-full" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Corporate Details */}
      <section className="py-16 md:py-24 px-6 md:px-12 border-t border-white/5" data-testid="corporate-section">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="border border-white/10 bg-card/5 p-8 md:p-12">
              <div className="flex items-center gap-4 mb-8">
                <Shield className="h-8 w-8 text-primary" strokeWidth={1.5} />
                <div>
                  <h2 className="font-serif text-2xl text-foreground">IP & Governance Summary</h2>
                  <p className="text-sm text-muted-foreground">Formal ownership and control</p>
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <p className="text-xs font-medium tracking-[0.15em] uppercase text-muted-foreground mb-2">
                    IP Ownership Statement
                  </p>
                  <p className="text-foreground">
                    All systems, frameworks, certifications, and methodologies are proprietary and 
                    owned by OnPoint Authority Systems, Inc. Usage is controlled through formal 
                    licensing agreements.
                  </p>
                </div>

                <div>
                  <p className="text-xs font-medium tracking-[0.15em] uppercase text-muted-foreground mb-2">
                    Licensing Framework
                  </p>
                  <p className="text-foreground">
                    Operators deploy authority systems under certification. Licensing includes 
                    training, standards compliance, and ongoing governance oversight. IP ownership 
                    remains with OnPoint Authority Systems, Inc.
                  </p>
                </div>

                <div>
                  <p className="text-xs font-medium tracking-[0.15em] uppercase text-muted-foreground mb-2">
                    Buyer Contact Protocol
                  </p>
                  <p className="text-foreground">
                    Qualified buyers and strategic partners may request access through the 
                    Authority Review process. Direct buyer inquiries are handled under NDA.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Purpose */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-[#050505]" data-testid="purpose-section">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
              Purpose
            </p>
            <h2 className="font-serif text-3xl md:text-4xl font-normal tracking-tight text-foreground mb-8">
              Remove Doubt,<br />
              <span className="text-primary">Not Generate Leads</span>
            </h2>
            <p className="text-lg font-light leading-relaxed text-muted-foreground mb-10">
              This information exists for verification purposes. It is not promotional material.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/legal">
                <Button
                  variant="outline"
                  className="border border-white/10 bg-transparent hover:border-primary/50 hover:text-primary uppercase tracking-[0.15em] text-xs font-bold px-6 py-4 rounded-none"
                >
                  <Shield className="h-4 w-4 mr-2" />
                  IP & Governance
                </Button>
              </Link>
              <Link to="/authority-review">
                <Button
                  className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.15em] text-xs font-bold px-6 py-4 rounded-none"
                  data-testid="credibility-cta"
                >
                  Authority Review
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CredibilityPage;
